/**
 * CommonPivotStrip — one-line summary above the Investigations events
 * table that surfaces how concentrated the active filter window is
 * across IP / JA4 / ASN / business / session dimensions, with the
 * single most concentrated dimension highlighted.
 *
 * Each chip pivots into a filtered Investigations view (where useful).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { fmtNumber } from "@/lib/format";
import type { CommonPivotV1 } from "@/types/v1";

const LABELS: Record<NonNullable<CommonPivotV1["strongest"]>, string> = {
  ja4: "JA4",
  asn: "ASN",
  ip: "IP",
  businessId: "business",
  sessionId: "session",
};

export function CommonPivotStrip() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["common-pivot", filters, tick],
    queryFn: ({ signal }) => api.commonPivot(filters, signal),
  });

  const data = q.data?.data;
  if (q.isLoading || !data || !data.events) return null;

  type Dim = NonNullable<CommonPivotV1["strongest"]>;
  const dims: { key: Dim; n: number }[] = ([
    { key: "ja4",        n: data.distinctJa4 },
    { key: "asn",        n: data.distinctAsn },
    { key: "ip",         n: data.distinctIp },
    { key: "businessId", n: data.distinctBusinessId },
    { key: "sessionId",  n: data.distinctSessionId },
  ] as { key: Dim; n: number }[]).filter((d) => d.n > 0);

  if (dims.length === 0) return null;

  const strongestLabel = data.strongest ? LABELS[data.strongest] : null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs text-fg-muted">
      <span>
        These <span className="font-semibold text-fg">{fmtNumber(data.events, { compact: true })}</span> events share
      </span>
      {dims.map((d, i) => {
        const isStrong = d.key === data.strongest;
        return (
          <span key={d.key} className="flex items-center gap-1">
            <span
              className={
                "rounded-md border px-1.5 py-0.5 font-medium " +
                (isStrong
                  ? "border-accent/60 bg-accent/10 text-accent"
                  : "border-border bg-surface-1 text-fg")
              }
              title={isStrong ? `Most concentrated dimension — strongest pivot signal` : undefined}
            >
              <span className="font-mono">{fmtNumber(d.n, { compact: true })}</span>{" "}
              <span className="text-[11px]">{LABELS[d.key]}{d.n === 1 ? "" : "s"}</span>
            </span>
            {i < dims.length - 1 && <span className="text-fg-subtle">·</span>}
          </span>
        );
      })}
      {strongestLabel && (
        <span className="ml-auto text-[11px] text-fg-subtle">
          Strongest signal: <span className="font-semibold text-fg">{strongestLabel}</span>
        </span>
      )}
    </div>
  );
}
